// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the transport, and the reason off-grid stays reachable
//  ---------------------------------------------------------------------------
//  Cloudflare is what ManDana syncs through. A nostr relay and a Blossom server
//  are what it may sync through later — or instead, or as well, on a mesh with
//  no Cloudflare in reach of it. The difference between that being a LATER FILE
//  and a LATER REWRITE is decided here, by whether anything above this line
//  ever learns which one it is talking to.
//
//  SEVEN VERBS, AND THE INTERFACE IS STILL SMALLER THAN CLOUDFLARE ON PURPOSE:
//
//    subscribe(room, onMessage, { onState }) -> { ok, unsubscribe }
//    publish(room, bytes, { delivery, coalesceKey }) -> { ok }
//    blobPut(bytes, {type,ref}) -> { ok, hash }
//    blobGet(hash)              -> { ok, bytes }
//    blobRelease(hash, ref)     -> { ok, collected }
//    blobList()                 -> { ok, blobs: [{ hash, size, type, uploaded }] }
//    blobUsage()                -> { ok, bytes, count, allowance }
//    close()
//
//  blobList IS BLOSSOM'S — BUD-02's GET /list/<pubkey> — and its rows are
//  NORMALISED on the way through, so nothing above this line learns that
//  Blossom spells the first field `sha256`. The sibling shape, not the same
//  field names, or a Blossom-ism ends up above the seam wearing our clothes.
//
//  blobUsage is DERIVABLE, and the seam derives it. A store that can list can
//  be summed; requiring both would make every transport write the same reduce,
//  and a store that knows the total cheaply (Cloudflare does — the server has
//  it already) should be able to say so instead. So blobList is required and
//  blobUsage is supplied when it is absent. `allowance: null` means THIS STORE
//  STATES NONE, which is the honest answer for anything summed from a list; a
//  number there would be a client constant impersonating a server's policy.
//
//  blobRelease was the missing one, and its absence was a defect rather than a
//  simplification: with put and get alone, nothing in the app could ever say
//  "this stored blob is no longer wanted", so a replaced profile picture stayed
//  on the server for good and so did a deleted account's. It is in the
//  INTERFACE rather than in the Cloudflare file because Blossom has it too —
//  DELETE /<sha256>, authorised by having uploaded it — so it is the sibling
//  shape and not a Cloudflare-ism.
//
//  What it releases is the CALLER'S hold, never the object directly. Whether
//  the object goes is the server's decision and depends on whether anybody else
//  is still keeping it; `collected` reports that and is not a success flag.
//  `collected: false` is the ordinary answer when two accounts uploaded the
//  same photograph, and a caller must not treat it as a failure.
//
//  A Durable Object's storage, its alarms, R2 metadata, conditional writes —
//  all of it deliberately unreachable from here. Every one is something a relay
//  cannot do, and the first caller to reach for one is the moment this stops
//  being an interface and becomes a Cloudflare client with extra steps.
//
//  THE BYTES ARE OPAQUE. Nothing here parses, decodes, validates or logs a
//  message body. A transport that inspected one could not be a relay that
//  cannot read, which is the one-way door the phase exists to protect. Bytes
//  in, the same bytes out — including bytes that are not valid UTF-8, which is
//  most of what a sealed envelope and a drawing look like.
//
//  THE HASH IS THE CLIENT'S. blobPut returns the name the client computed, not
//  one a server assigned. That IS content-addressing, and it is what lets the
//  same blob be fetched from R2 today and from a Blossom server tomorrow with
//  nothing above this line changing. SHA-256, in lowercase hex, which is what
//  Blossom names a blob by.
//
//  It is NOT mdFileHash (md-file.ts:186). That one is two 32-bit hashes and a
//  length, which is exactly right for "have I already stored this locally?" and
//  wrong for a name a stranger can choose the preimage of. Two hashes for two
//  jobs; the local one keeps its callers.
//
//  THE THREE PROMISES, inherited from md-engine.js, because a floor that breaks
//  them makes everything above it a lie: it never throws, it always reports,
//  and it never leaves a caller waiting.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  /* `close` is in the contract and is not a verb the app calls in anger. It is
     here because a transport holds a socket, and an interface that cannot be
     let go of leaks one per room per navigation. */
  const MD_TRANSPORT_CONTRACT = ['subscribe', 'publish', 'blobPut', 'blobGet',
    'blobGetRange', 'blobRelease', 'blobList', 'blobUsage', 'close'];
  const MD_DELIVERY = Object.freeze({ EPHEMERAL: 'ephemeral', RELIABLE: 'reliable' });

  /* A verb the seam can build out of another one. The value is what it needs;
     a transport satisfies the contract by implementing EITHER. Kept as a map
     rather than a list of special cases so the next derived verb is a line
     here and not a branch in the checker.

     blobGetRange is derived rather than required because a range read is a
     property of a STORE, not of the design. R2 has one, Blossom has one, and a
     plain static file server has one — but a relay that hands back whole blobs
     is still a perfectly good place to keep bytes, and refusing it over an
     optimisation would be the seam deciding which stores are allowed to exist.
     The built version fetches whole and slices, which is not a stub: the unit
     that travels is an 8 MB chunk, so a caller asking for a window inside one
     chunk gets the right bytes at the right offset either way. What a real
     range read buys is the last 8 MB, not correctness. */
  const MD_TRANSPORT_DERIVED: Record<string, string> = {
    blobUsage: 'blobList',
    blobGetRange: 'blobGet',
  };

  const factories = new Map<string, (options?: any) => any>();

  /** Does this object satisfy the interface, and if not, which parts are absent?
      Named parts rather than a bare false: the alternative is a TypeError in a
      room somebody is drawing on.

      It also names what is on the object that is NOT on the contract, and that
      half is about a different failure. A transport can implement all eight
      verbs and still hand back `refreshCloudflareToken()` — perfectly good
      code, and an open door: the first caller above the line to reach for it
      has made the swap to another store a rewrite again, one line at a time,
      with nothing anywhere reporting that it happened.

      `extra` is REPORTED, NOT REFUSED. A transport may legitimately want its
      own internals, and a seam that rejected them would push the same methods
      one level down into a closure where nothing can see them at all. The
      point is visibility: a verb off the contract should be a thing somebody
      decided, and `tests/seam.test.js` asserts the shipped transport has none.

      Functions only. A field is state, and state was never reachable as a verb. */
  function mdTransportCheck(
    candidate: unknown,
  ): { ok: boolean; missing: string[]; extra: string[] } {
    if (!candidate || typeof candidate !== 'object') {
      return { ok: false, missing: MD_TRANSPORT_CONTRACT, extra: [] };
    }
    const has = (verb: string) => typeof (candidate as any)[verb] === 'function';
    const missing = MD_TRANSPORT_CONTRACT.filter(
      (verb) => !has(verb) && !(MD_TRANSPORT_DERIVED[verb] && has(MD_TRANSPORT_DERIVED[verb])),
    );
    /* for..in rather than Object.keys, so a verb installed on a prototype is
       just as visible as one written into the literal — which is exactly how
       complete() installs the derived one, and how a factory returning a class
       instance would ship every method it has. */
    const extra: string[] = [];
    for (const key in candidate as any) {
      if (typeof (candidate as any)[key] !== 'function') continue;
      if (MD_TRANSPORT_CONTRACT.includes(key)) continue;
      extra.push(key);
    }
    return { ok: missing.length === 0, missing, extra };
  }

  function mdTransportRegister(name: string, factory: (options?: any) => any): void {
    if (typeof name !== 'string' || !name || typeof factory !== 'function') return;
    factories.set(name, factory);
  }

  function mdTransportNames(): string[] {
    return [...factories.keys()];
  }

  /* ── which transport, asked in one place ────────────────────────────────
     The registry above resolves a name; this is where the name comes from.
     It lives here rather than in each consumer because a promise kept in two
     places is a promise kept in none — and the day a nostr or Blossom
     transport is registered, choosing it must be ONE edit rather than one per
     caller, with the caller somebody missed going on talking to Cloudflare.
     That failure looks exactly like working software until it reaches the
     device with no Cloudflare in reach of it.

     IN MEMORY, NEVER WRITTEN DOWN, deliberately. A stored choice would be a
     document, a document would sync, and a device on a mesh would then tell a
     device on the cloud which store to use. Which transport is reachable is a
     fact about a device at a moment, not about an account. Whatever decides
     says so at boot; until something does, the answer is what ships. */
  const MD_TRANSPORT_DEFAULT = 'cf';
  let chosen = MD_TRANSPORT_DEFAULT;

  /** Which transport this device is talking to. */
  function mdTransportName(): string {
    return chosen;
  }

  /** Choose one. An empty name means "the shipped one"; anything that is not a
      name at all is refused rather than accepted as one, because a caller that
      passed undefined by accident should not silently take the app off its
      transport. The refusal leaves the last good answer standing. */
  function mdTransportUse(name: unknown): boolean {
    if (typeof name !== 'string') return false;
    chosen = name || MD_TRANSPORT_DEFAULT;
    return true;
  }

  /** Tests only — module state, and a test inheriting the previous one's
      registry, or its choice of transport, would pass or fail for reasons it
      cannot see. */
  function mdTransportForget(): void {
    factories.clear();
    chosen = MD_TRANSPORT_DEFAULT;
  }

  /* The factory may be sync or async, and may fail in either shape. All three
     land here as a value, because a caller of a transport is a surface with a
     person looking at it and "could not connect" is a sentence it can show. */
  async function mdTransportOpen(name: string, options?: any) {
    const factory = factories.get(name);
    if (!factory) {
      return { ok: false, error: `there is no transport called ${JSON.stringify(name)}` };
    }
    let made: any = null;
    try {
      made = await factory(options);
    } catch (err: any) {
      return { ok: false, error: (err && err.message) || String(err) };
    }
    const check = mdTransportCheck(made);
    if (!check.ok) {
      return {
        ok: false,
        error: `the ${name} transport does not implement ${check.missing.join(', ')}`,
      };
    }
    return { ok: true, transport: complete(made) };
  }

  /* ── the derived half of the contract ────────────────────────────────────
     Installed on what the CALLER receives and never written onto the object a
     factory returned. A seam that changed the thing it was handed is a seam
     with side effects, and the symptom is a transport that gained a method
     between two tests. Object.create keeps `this` reaching the original's own
     state, so a transport's blobList still sees its own fields. */
  function complete(made: any): any {
    const build: Record<string, any> = { blobUsage: usageFromList, blobGetRange: rangeFromGet };
    const owed = Object.keys(MD_TRANSPORT_DERIVED).filter(
      (verb) => typeof made[verb] !== 'function',
    );
    if (!owed.length) return made;
    const wrapper = Object.create(made);
    for (const verb of owed) wrapper[verb] = build[verb];
    return wrapper;
  }

  /** A window, for a store that only knows how to hand over the whole thing.

      It costs the whole blob, and that is the honest price rather than a
      failure: the caller above is asking for a range of a CHUNK, and a chunk
      is 8 MB by construction (md-blobs.ts). Fetching 8 MB to answer a question
      about 40 KB of it is the worst case, and it is bounded — which is the
      difference between this and the thing 1.15 exists to prevent, where
      seeking into a film cost the film.

      The window is CLAMPED to what came back rather than trusted. A caller
      that asked past the end gets the bytes that exist, not an exception and
      not a buffer with invented zeroes on the end of it. */
  async function rangeFromGet(this: any, hash: string, start: number, end: number): Promise<any> {
    let got: any = null;
    try { got = await this.blobGet(hash); }
    catch (err: any) { return { ok: false, reason: 'unreachable' }; }
    if (!got || !got.ok || !got.bytes || typeof got.bytes.length !== 'number') {
      return { ok: false, reason: (got && got.reason) || 'unreachable' };
    }
    const size = got.bytes.length;
    const from = Math.max(0, Math.min(Number(start) || 0, size));
    const to = Math.max(from, Math.min(Number(end), size));
    return { ok: true, bytes: got.bytes.slice(from, to), type: got.type };
  }

  /** Sum what the store lists. Every refusal is `unreachable` unless the list
      named its own, because the one thing this must never do is answer zero
      when it does not know: "I could not ask" and "you are using nothing" are
      different sentences and only one of them is safe to show somebody. */
  async function usageFromList(this: any): Promise<any> {
    let listed: any = null;
    try { listed = await this.blobList(); }
    catch (err: any) { return { ok: false, reason: 'unreachable' }; }
    if (!listed || !listed.ok || !Array.isArray(listed.blobs)) {
      return { ok: false, reason: (listed && listed.reason) || 'unreachable' };
    }
    /* By hash, because content addressing means one object however many
       records point at it — a store that lists a hash once per holder would
       otherwise report an account at several times what it actually holds. */
    const sizes = new Map<string, number>();
    for (const row of listed.blobs) {
      if (!row || typeof row !== 'object') continue;
      if (!mdTransportIsHash(row.hash)) continue;
      if (typeof row.size !== 'number' || !Number.isFinite(row.size) || row.size < 0) continue;
      if (!sizes.has(row.hash)) sizes.set(row.hash, row.size);
    }
    let bytes = 0;
    for (const size of sizes.values()) bytes += size;
    return { ok: true, bytes, count: sizes.size, allowance: null };
  }

  /* ── why a store said no ─────────────────────────────────────────────────
     REFUSALS ARE NAMED, NEVER NUMBERED. A status code is a Cloudflare fact,
     and the first caller above this line to read one is the moment the seam
     stops being a seam. So a transport maps its own refusals into these words
     and nothing above ever learns what a 413 is.

     The vocabulary is small on purpose, and the split through it is the one
     thing callers actually need: exactly one of these is worth trying again.

       unreachable     the store was not there. A network that went, an origin
                       whose Worker predates the route, a binding that is not
                       bound yet. It comes back, and the queue should wait.

       too_large       the request body exceeded what the platform carries
       over_allowance  the account has spent its room
       unauthorised    the session is gone, or was never for this store
                       (the server spells its own answer `unauthorized`, in
                       HTTP's spelling; this is the client's word for it)
       hash_mismatch   the bytes are not what they were named
       bad_ref         the hold named a reference that is not one

     The last five will refuse identically for ever. A queue that keeps
     offering them is a queue that never empties, a battery that never rests,
     and a person who is never told — so they are TERMINAL, which means
     surfaced (1.17 offers cancel, not retry).

     AN UNKNOWN REASON IS TERMINAL TOO, for the same reason rather than a
     different one: surfacing something this build does not understand beats
     spinning on it forever in silence. */
  const MD_BLOB_REASONS = ['unreachable', 'too_large', 'over_allowance',
    'unauthorised', 'hash_mismatch', 'bad_ref'];

  const MD_BLOB_RETRYABLE = ['unreachable'];

  /** Is this refusal worth offering again? See the vocabulary above. */
  function mdTransportRetryable(reason: unknown): boolean {
    return typeof reason === 'string' && MD_BLOB_RETRYABLE.includes(reason);
  }

  /* ── the name of a piece of content ─────────────────────────────────────── */
  const HEX = /^[0-9a-f]{64}$/;

  /** Lowercase hex, and only lowercase: one spelling, or the same blob has two
      names and the store holds it twice. This also runs before a hash reaches a
      URL, which is what keeps `../` out of a bucket key. */
  function mdTransportIsHash(value: unknown): boolean {
    return typeof value === 'string' && HEX.test(value);
  }

  async function mdTransportHash(bytes: Uint8Array): Promise<string> {
    const subtle = g.crypto && g.crypto.subtle;
    if (!subtle) return '';
    const digest = await subtle.digest('SHA-256', bytes);
    let out = '';
    for (const b of new Uint8Array(digest)) out += b.toString(16).padStart(2, '0');
    return out;
  }

  Object.assign(g, {
    MD_TRANSPORT_CONTRACT,
    MD_DELIVERY,
    MD_TRANSPORT_DERIVED,
    MD_TRANSPORT_DEFAULT,
    mdTransportName,
    mdTransportUse,
    MD_BLOB_REASONS,
    MD_BLOB_RETRYABLE,
    mdTransportRetryable,
    mdTransportCheck,
    mdTransportRegister,
    mdTransportNames,
    mdTransportOpen,
    mdTransportForget,
    mdTransportHash,
    mdTransportIsHash,
  });

  // CommonJS escape hatch, following md-grant.ts and md-crypto.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = {
      MD_TRANSPORT_CONTRACT, MD_TRANSPORT_DERIVED, MD_DELIVERY,
      MD_TRANSPORT_DEFAULT, mdTransportName, mdTransportUse,
      MD_BLOB_REASONS, MD_BLOB_RETRYABLE, mdTransportRetryable,
      mdTransportCheck, mdTransportRegister, mdTransportNames,
      mdTransportOpen, mdTransportForget, mdTransportHash, mdTransportIsHash,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
